/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import { Link } from 'react-router-dom';
import { generateIteratorKey as keygen } from '../../utils/helpers';
import { PARTICIPANT_STATUS } from '../../constants/consentStatus';
import * as consts from '../../const';
import {
  participant,
  userManagementStudyParticipant,
  userManagementOrgStudyParticipant
} from '../../constants/localUrls';
import moment from 'moment';

// wrapColumn :: String | JSX -> JSX
const wrapColumn = (column) =>
  function wrapColumn() {
    return <div className="participant-col">{column}</div>;
  };

// sortByUpdatedOn :: [Object] -> [Object]
const sortByUpdatedOn = (items) =>
  cloneDeep(items).sort((_a, _b) => {
    const a = new Date(_a.updatedOn).getTime();
    const b = new Date(_b.updatedOn).getTime();

    return b > a ? 1 : b < a ? -1 : 0;
  });

// sortStatuses :: [String] -> {String}
const sortStatuses = (statuses) =>
  statuses.sort((_a, _b) => {
    const a = PARTICIPANT_STATUS[_a];
    const b = PARTICIPANT_STATUS[_b];

    return b < a ? 1 : b > a ? -1 : 0;
  });

// userColumn :: Object -> JSX
const userColumn = ({ study, userManagementFlow, userManagementOrgFlow, row }) => {
  const { organizationId, organizationName } = userManagementOrgFlow || {};
  const pathname = userManagementFlow
    ? userManagementStudyParticipant(row.participantJourneyGid)
    : userManagementOrgFlow
      ? userManagementOrgStudyParticipant(organizationId, organizationName, row.participantJourneyGid)
      : participant(row.participantJourneyGid);

  const participantJourneyGid = row.participantJourneyGid;
  const participantId = row.userId;
  const participantName = row.participantUsername || '';
  const studyName = (study && study.name) || '';
  const studyGid = (study && study.gid) || '';

  const forBreadcrumb = {
    participantJourneyGid,
    studyName,
    studyGid,
    participantId,
    organizationId,
    participantName,
    organizationName
  };

  return (
    <Link className="link" to={{ pathname, state: { forBreadcrumb } }}>
      {participantName}
    </Link>
  );
};

userColumn.propTypes = {
  study: PropTypes.object,
  userManagementFlow: PropTypes.bool,
  userManagementOrgFlow: PropTypes.bool,
  row: PropTypes.object
};

// actionsNeededColumn :: Object -> JSX
const actionsNeededColumn = (item) => {
  const actionsNeeded = item.actionsNeeded || [];
  return (
    <div className="participant-col">
      {actionsNeeded.length === 0 ? (
        <div className="actions-needed-empty">{consts.NONE}</div>
      ) : (
        actionsNeeded.map((action) => (
          <div key={action} className="participant-col-action-needed" title={consts[action]}>
            {action === 'REVIEW_NEW_VERSION_OF_OPTIONAL_DOC' && <i className="material-icons orange">warning</i>}
            {action === 'READY_FOR_Q_A' && <i className="material-icons orange">assignment_late</i>}
            {action === 'AWAITING_FOR_INVESTIGATOR_SIGNATURE' && (
              <i className="material-icons orange">assignment_late</i>
            )}
            {consts[action]}
          </div>
        ))
      )}
    </div>
  );
};

// statusToColor :: String -> String
const statusToColor = (status) => {
  switch (status) {
    case 'CONSENTED':
      return '#04871C';
    case 'WAITING_CONSENT':
    case 'WAITING_RECONSENT':
      return '#F09C00';
    case 'STUDY_COMPLETED':
      return '#B4B1B1';
    default:
      return '#B4B1B1';
  }
};

// statusColumn :: Object -> JSX
const statusColumn = (row) => [
  <div key="status-labels" style={{ color: statusToColor(row.status) }}>
    {PARTICIPANT_STATUS[row.status]}
  </div>
];

const updatedOnColumn = (row) => {
  return (
    <div key="status-updated-on" className="participant-updated-on">
      {moment(row.updatedOn).fromNow()}
    </div>
  );
};

const missingSubjectNo = () => <label className="subject-number-empty">{consts.SUBJECT_NUMBER_EMPTY}</label>;

class ParticipantsService {
  static columns = {
    userId: consts.PARTICIPANT_ID,
    status: consts.STATUS,
    lastChanged: consts.LAST_CHANGED,
    actions: consts.ACTIONS_NEEDED,
    subjectNumber: consts.STUDY_SUBJECT_NUMBER,
    optionalDocuments: consts.OPTIONAL_DOCUMENTS,
    participantName: consts.PARTICIPANT_USERNAME,
    site: consts.PARTICIPANT_SITE,
    latestStatusChange: consts.PARTICIPANT_LATEST_STATUS_CHANGE
  };

  // buildParticipantDocumentsContent :: (Object, [Object], Boolean, Boolean) -> [Object]
  static buildParticipantDocumentsContent(study, items, userManagementFlow = false, userManagementOrgFlow = null) {
    if (!items) return [];

    const buildParticipantRow = (row, index) => ({
      id: `grid-row-${row.userId}`,
      columns: [
        {
          id: `${index}-${row.userId}`,
          columnData: wrapColumn(userColumn({ study, userManagementFlow, userManagementOrgFlow, row }))
        },
        { id: `${index}-${row.userId}`, columnData: wrapColumn(row.subjectNumber || missingSubjectNo()) },
        { id: `${index}-${row.userId}`, columnData: wrapColumn(row.site) },
        { id: `${index}-${row.userId}`, columnData: wrapColumn(statusColumn(row)) },
        { id: `${index}-${row.userId}`, columnData: wrapColumn(updatedOnColumn(row)) },
        { id: `${index}-${row.userId}`, columnData: wrapColumn(actionsNeededColumn(row)) }
      ]
    });

    return sortByUpdatedOn(items).map(buildParticipantRow);
  }

  // createParticipantStatusesSelection :: Object -> [Object]
  static createParticipantStatusesSelection(statuses) {
    if (!statuses || !statuses.data) return [];

    const defaultOption = { value: '', label: <div {...keygen()}>{consts.SELECT_VALUE_ALL_STATUSES}</div> };
    const orderedStatuses = sortStatuses(cloneDeep(statuses.data.statuses || []));
    const addStatusOption = (status) => ({
      value: status,
      label: (
        <div {...keygen()} style={{ color: statusToColor(status) }}>
          {PARTICIPANT_STATUS[status]}
        </div>
      )
    });

    return orderedStatuses.reduce((acc, status) => acc.concat(addStatusOption(status)), [defaultOption]);
  }

  // createOrganizationsSelection :: Object -> [Object]
  static createOrganizationsSelection(organizations) {
    if (!organizations || !organizations.organizationsByStudy) return [];

    const defaultOption = { value: '', label: <div {...keygen()}>{consts.ALL_SITES}</div> };
    const organizationsData = organizations.organizationsByStudy.data || [];
    const addOrganizationOption = (org) => ({ value: org.org.gid, label: <div {...keygen()}>{org.org.name}</div> });

    return organizationsData.reduce((acc, org) => acc.concat(addOrganizationOption(org)), [defaultOption]);
  }

  // createOptionalDocumentSelection :: () -> [Object]
  static createOptionalDocumentSelection() {
    const optionalDocumentsOptions = [
      { value: '', label: <div {...keygen()}>{consts.ALL_ACTIONS_NEEDED}</div> },
      {
        value: 'REVIEW_NEW_VERSION_OF_OPTIONAL_DOC',
        label: (
          <div {...keygen()}>
            <i className="material-icons orange">warning</i>
            {consts.REVIEW_NEW_VERSION_OF_OPTIONAL_DOC}
          </div>
        )
      },
      {
        value: 'READY_FOR_Q_A',
        label: (
          <div {...keygen()}>
            <i className="material-icons orange">assignment_late</i>
            {consts.READY_FOR_Q_A}
          </div>
        )
      },
      {
        value: 'AWAITING_FOR_INVESTIGATOR_SIGNATURE',
        label: (
          <div {...keygen()}>
            <i className="material-icons orange">assignment_late</i>
            {consts.AWAITING_FOR_INVESTIGATOR_SIGNATURE}
          </div>
        )
      }
    ];

    return optionalDocumentsOptions;
  }
}

export default ParticipantsService;
